import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './media-controls-item.module.scss';

export interface MediaControlsItemProps {
  id: string;
  icon?: string;
  text?: string;
  tag?: ReactElement | null;
  tagContainerClassName?: string;
  className?: string;
  selectedClasses?: string;
  textColorClasses?: string;
  textPaddingClasses?: string;
  iconClasses?: string;
  isTextBold?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  isSelected?: boolean;
  showAsListItem?: boolean;
  isDisabled?: boolean;
  onClick?(id: string): void;
  customIconObject?: ReactElement;
}

export function MediaControlsItem({
  tag = null,
  text = '',
  icon = '',
  className = '',
  tagContainerClassName = '',
  selectedClasses = '',
  textColorClasses = '',
  textPaddingClasses = '',
  iconClasses = '',
  loadingText = '',
  isTextBold = false,
  isLoading = false,
  isSelected = false,
  showAsListItem = false,
  onClick = (): void => {},
  isDisabled = false,
  customIconObject = undefined,
  ...props
}: MediaControlsItemProps): ReactElement | null {
  const getTextColorClasses = (): string => {
    if (isDisabled) {
      return 'color-extra-light';
    }

    if (textColorClasses && textColorClasses !== '') {
      return textColorClasses;
    }

    return isSelected ? 'content-primary' : 'content-sub-text';
  };

  const getTextPaddingClasses = (): string => {
    if (textPaddingClasses && textPaddingClasses !== '') {
      return textPaddingClasses;
    }

    return 'spacing-p-r-6 spacing-p-l-6 ';
  };

  const getIconClasses = (): string => {
    if (iconClasses && iconClasses !== '') {
      return iconClasses;
    }
    return '';
  };

  const getContent = (): ReactElement => {
    const textVal = isLoading ? loadingText ?? window.i18next.t('pmwjs_loading') : text;
    return (
      <>
        {!isLoading ? (
          <>{customIconObject ?? <Icon icon={icon} className={getIconClasses()} size={showAsListItem ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24} />}</>
        ) : (
          <CircularProgressLoader classes="_unmargin" size={showAsListItem ? LOADER_SIZE.SMALL : LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
        )}
        <Text
          title={textVal}
          val={textVal}
          className={`${showAsListItem ? styles.textMarginLeft : 'spacing-m-t-1 '} ${getTextPaddingClasses()} ${styles.text} ${getTextColorClasses()}`}
          size={TextSize.XXSMALL}
          bold={isTextBold}
        />
      </>
    );
  };

  const getTag = (): ReactElement | null => {
    return tag ? <div className={`${showAsListItem ? '' : `${styles.gridTagContainer} spacing-m-r-1 spacing-m-l-1 spacing-m-t-1`} ${tagContainerClassName}`}>{tag}</div> : null;
  };

  const getSelectedStateClasses = (): string => {
    if (isSelected) {
      return selectedClasses === '' ? styles.selected : selectedClasses;
    }
    return '';
  };

  const showAsGridItem = (): ReactElement => {
    return (
      <li
        onClick={(): void => {
          onClick(props.id);
        }}
        id={props.id}
        className={`${styles.baseContainer} flex-center spacing-p-b-0 hover-transition-all ${styles.gridContainer} ${className} ${isDisabled ? styles.isDisabled : ''} ${getSelectedStateClasses()}`}
      >
        <div className="flex-column-align-center">{getContent()}</div>
        {getTag()}
      </li>
    );
  };

  const showAsList = (): ReactElement => {
    return (
      <li
        onClick={(): void => {
          if (!isLoading) {
            onClick(props.id);
          }
        }}
        id={props.id}
        className={`${styles.baseContainer} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.isDisabled : ''} ${
          styles.listContainer
        } spacing-p-2 flex-row-justify-between flex-items-center hover-transition-all ${className}`}
      >
        <div className="flex-row-align-center">{getContent()}</div>
        {getTag()}
      </li>
    );
  };

  const showMediaItem = (): ReactElement => {
    return showAsListItem ? showAsList() : showAsGridItem();
  };

  return showMediaItem();
}
